import { useState, useEffect, useRef } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Card from '../../components/base/Card';
import Button from '../../components/base/Button';
import { createClient } from '@supabase/supabase-js';
import { useSearchParams } from 'react-router-dom';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseKey);

interface Message {
  id: string;
  order_id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
}

interface ContactMessage {
  id: string;
  contact_id: string;
  sender_type: 'user' | 'admin';
  message: string;
  created_at: string;
}

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  amount: number;
  currency: string;
  status: string;
  created_at: string;
}

interface Contact {
  id: string;
  customer_name: string;
  customer_email: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
}

export default function MessagesPage() {
  const [searchParams] = useSearchParams();
  const [viewType, setViewType] = useState<'order' | 'contact'>('order');
  const [orders, setOrders] = useState<Order[]>([]);
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [contactMessages, setContactMessages] = useState<ContactMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [searchId, setSearchId] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Debug: Log when component mounts
  useEffect(() => {
    console.log('=== MessagesPage Component Mounted ===');
    console.log('Current URL:', window.location.href);
    console.log('Search Params:', window.location.search);
  }, []);

  // Get ID and type from URL parameter
  useEffect(() => {
    console.log('=== URL Parameter Effect Running ===');
    console.log('Raw URL Parameters:', {
      order: searchParams.get('order'),
      contact: searchParams.get('contact')
    });
    
    const orderParam = searchParams.get('order');
    const contactParam = searchParams.get('contact');
    
    console.log('Order:', orderParam);
    console.log('Contact:', contactParam);
    
    if (orderParam) {
      // Remove '#' symbol if present
      const decodedId = decodeURIComponent(orderParam).replace(/^#/, '');
      console.log('Final Order ID for Search:', decodedId);
      setSearchId(decodedId);
      setViewType('order');
      setTimeout(() => {
        console.log('Fetching order for:', decodedId);
        fetchOrderWithId(decodedId);
      }, 100);
    } else if (contactParam) {
      // Remove '#' symbol if present
      const decodedId = decodeURIComponent(contactParam).replace(/^#/, '');
      console.log('Final Contact ID for Search:', decodedId);
      setSearchId(decodedId);
      setViewType('contact');
      setTimeout(() => {
        console.log('Fetching contact for:', decodedId);
        fetchContactWithId(decodedId);
      }, 100);
    } else {
      console.log('No order or contact parameter found in URL');
    }
  }, [searchParams]);

  // Fetch user's order with ID parameter
  const fetchOrderWithId = async (orderId: string) => {
    if (!orderId) {
      console.log('No order ID provided');
      return;
    }
    
    setLoading(true);
    try {
      // Remove '#' symbol if present and trim
      const trimmedId = orderId.replace(/^#/, '').trim();
      console.log('Fetching order with ID:', trimmedId);
      
      // Try to search by order_number first, then by id (UUID)
      let { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('order_number', trimmedId);

      // If not found by order_number, try searching by id (UUID)
      if (!data || data.length === 0) {
        const result = await supabase
          .from('orders')
          .select('*')
          .eq('id', trimmedId);
        
        data = result.data;
        error = result.error;
      }

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Order query result:', data);
      
      if (!data || data.length === 0) {
        console.log('No order found');
        setOrders([]);
        alert('注文が見つかりませんでした。Order IDを確認してください。');
        return;
      }
      
      const foundOrder = data[0];
      console.log('Order found:', foundOrder);
      setOrders([foundOrder]);
      
      // Auto-select the order if available
      console.log('Auto-selecting order');
      await handleOrderSelect(foundOrder);
    } catch (error) {
      console.error('Error fetching order:', error);
      alert('注文が見つかりませんでした。Order IDを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's contact with ID parameter
  const fetchContactWithId = async (contactId: string) => {
    if (!contactId) {
      console.log('No contact ID provided');
      return;
    }
    
    setLoading(true);
    try {
      // Remove '#' symbol if present and trim
      const trimmedId = contactId.replace(/^#/, '').trim();
      console.log('=== Fetching Contact ===');
      console.log('Contact ID:', trimmedId);
      
      const { data, error } = await supabase
        .from('contacts')
        .select('*')
        .eq('id', trimmedId);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Contact query result:', data);
      
      if (!data || data.length === 0) {
        console.log('No contact found');
        setContacts([]);
        alert('お問い合わせが見つかりませんでした。Contact IDを確認してください。');
        return;
      }
      
      const foundContact = data[0];
      console.log('Contact found:', foundContact);
      setContacts([foundContact]);
      
      // Auto-select the contact if available
      console.log('Auto-selecting contact:', foundContact);
      await handleContactSelect(foundContact);
    } catch (error) {
      console.error('Error fetching contact:', error);
      alert('お問い合わせが見つかりませんでした。Contact IDを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  // Fetch user's orders with order number or ID parameter
  const fetchOrdersWithOrderNumber = async (orderNumber: string) => {
    if (!orderNumber) {
      return;
    }
    
    setLoading(true);
    try {
      // Remove '#' symbol if present and trim
      const trimmedOrderNumber = orderNumber.replace(/^#/, '').trim();
      
      // Try to search by order_number first, then by id
      let { data, error } = await supabase
        .from('orders')
        .select('*')
        .eq('order_number', trimmedOrderNumber)
        .single();

      // If not found by order_number, try searching by id (UUID)
      if (error && error.code === 'PGRST116') {
        const { data: dataById, error: errorById } = await supabase
          .from('orders')
          .select('*')
          .eq('id', trimmedOrderNumber)
          .single();
        
        data = dataById;
        error = errorById;
      }

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      setOrders(data ? [data] : []);
      
      // Auto-select the order if available
      if (data) {
        await handleOrderSelect(data);
      }
    } catch (error) {
      console.error('Error fetching order:', error);
      alert('注文が見つかりませんでした。Order IDを確認してください。');
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders based on order number or ID
  const fetchData = async () => {
    // Remove '#' symbol if present and trim
    const trimmedId = searchId.replace(/^#/, '').trim();
    if (!trimmedId) {
      alert(viewType === 'order' ? 'Order IDを入力してください。' : 'Contact IDを入力してください。');
      return;
    }
    
    if (viewType === 'order') {
      await fetchOrderWithId(trimmedId);
    } else {
      await fetchContactWithId(trimmedId);
    }
  };

  // Fetch messages for selected order
  const fetchMessages = async (orderId: string) => {
    try {
      console.log('Fetching messages for order:', orderId);
      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('order_id', orderId)
        .order('created_at', { ascending: true });

      if (error) throw error;
      console.log('Messages found:', data?.length || 0);
      setMessages(data || []);
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // Fetch messages for selected contact
  const fetchContactMessages = async (contactId: string) => {
    try {
      console.log('=== Fetching Contact Messages ===');
      console.log('Contact ID:', contactId);
      
      const { data, error } = await supabase
        .from('contact_messages')
        .select('*')
        .eq('contact_id', contactId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Contact messages found:', data?.length || 0);
      console.log('Contact messages data:', data);
      setContactMessages(data || []);
    } catch (error) {
      console.error('Error fetching contact messages:', error);
    }
  };

  // Send order message
  const sendOrderMessage = async () => {
    if (!newMessage.trim() || !selectedOrder) return;

    try {
      const { error } = await supabase
        .from('messages')
        .insert([
          {
            order_id: selectedOrder.id,
            sender_type: 'user',
            message: newMessage.trim()
          }
        ]);

      if (error) throw error;
      
      setNewMessage('');
      await fetchMessages(selectedOrder.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('メッセージの送信に失敗しました。もう一度お試しください。');
    }
  };

  // Send contact message
  const sendContactMessage = async () => {
    if (!newMessage.trim() || !selectedContact) return;

    try {
      console.log('=== Sending Contact Message ===');
      console.log('Contact ID:', selectedContact.id);
      console.log('Message:', newMessage.trim());
      
      const { error } = await supabase
        .from('contact_messages')
        .insert([
          {
            contact_id: selectedContact.id,
            sender_type: 'user',
            message: newMessage.trim()
          }
        ]);

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }
      
      console.log('Message sent successfully');
      setNewMessage('');
      await fetchContactMessages(selectedContact.id);
    } catch (error) {
      console.error('Error sending message:', error);
      alert('メッセージの送信に失敗しました。もう一度お試しください。');
    }
  };

  // Handle order selection
  const handleOrderSelect = async (order: Order) => {
    console.log('Order selected:', order);
    setSelectedOrder(order);
    setSelectedContact(null);
    await fetchMessages(order.id);
  };

  // Handle contact selection
  const handleContactSelect = async (contact: Contact) => {
    console.log('=== Contact Selected ===');
    console.log('Contact:', contact);
    setSelectedContact(contact);
    setSelectedOrder(null);
    await fetchContactMessages(contact.id);
  };

  // Handle view type change
  const handleViewTypeChange = (type: 'order' | 'contact') => {
    console.log('View type changed to:', type);
    setViewType(type);
    setSelectedOrder(null);
    setSelectedContact(null);
    setMessages([]);
    setContactMessages([]);
    setOrders([]);
    setContacts([]);
    setSearchId('');
  };

  // Auto scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, contactMessages]);

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-600';
      case 'deposited': return 'bg-blue-600';
      case 'sent': return 'bg-green-600';
      case 'cancelled': return 'bg-red-600';
      case 'open': return 'bg-blue-600';
      case 'closed': return 'bg-gray-600';
      default: return 'bg-gray-600';
    }
  };

  // Get status text
  const getStatusText = (status: string) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'deposited': return 'Deposited';
      case 'sent': return 'Sent';
      case 'cancelled': return 'Cancelled';
      case 'open': return 'Open';
      case 'closed': return 'Closed';
      default: return status;
    }
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Bangkok'
    });
  };

  const currentMessages = viewType === 'order' ? messages : contactMessages;
  const selectedItem = viewType === 'order' ? selectedOrder : selectedContact;

  console.log('=== Current State ===');
  console.log('View Type:', viewType);
  console.log('Selected Contact:', selectedContact);
  console.log('Contact Messages:', contactMessages);
  console.log('Contacts List:', contacts);

  return (
    <div className="min-h-screen bg-gray-900">
      <Header />
      
      <main className="py-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Messages
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Communicate with our support team. Track status updates and get assistance.
            </p>
          </div>
        </section>

        {/* Messages Section */}
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* List Sidebar */}
              <div className="lg:col-span-1">
                <Card className="p-6 h-full">
                  {/* View Type Tabs */}
                  <div className="flex space-x-2 mb-6 bg-gray-800 p-1 rounded-lg">
                    <button
                      onClick={() => handleViewTypeChange('order')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
                        viewType === 'order'
                          ? 'bg-teal-600 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <i className="ri-shopping-cart-line mr-2"></i>
                      Orders
                    </button>
                    <button
                      onClick={() => handleViewTypeChange('contact')}
                      className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap text-sm ${
                        viewType === 'contact'
                          ? 'bg-teal-600 text-white'
                          : 'text-gray-400 hover:text-white'
                      }`}
                    >
                      <i className="ri-customer-service-line mr-2"></i>
                      Contacts
                    </button>
                  </div>

                  <h2 className="text-xl font-bold text-white mb-6">
                    {viewType === 'order' ? 'Your Order' : 'Your Inquiry'}
                  </h2>
                  
                  {/* ID Search */}
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      {viewType === 'order' ? 'Enter your Order ID' : 'Enter your Contact ID'}
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={searchId}
                        onChange={(e) => setSearchId(e.target.value)}
                        className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                        placeholder={viewType === 'order' ? 'Order ID' : 'Contact ID'}
                      />
                      <Button 
                        onClick={fetchData}
                        disabled={!searchId || loading}
                        className="whitespace-nowrap"
                      >
                        {loading ? (
                          <i className="ri-loader-4-line animate-spin"></i>
                        ) : (
                          <i className="ri-search-line"></i>
                        )}
                      </Button>
                    </div>
                  </div>

                  {/* Orders List */}
                  {viewType === 'order' && (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {orders.length === 0 && searchId && !loading && (
                        <div className="text-center py-8">
                          <i className="ri-inbox-line text-4xl text-gray-600 mb-3"></i>
                          <p className="text-gray-400">No order found with this ID</p>
                        </div>
                      )}
                      
                      {orders.map((order) => (
                        <button
                          key={order.id}
                          onClick={() => handleOrderSelect(order)}
                          className={`w-full text-left p-4 rounded-lg border transition-all cursor-pointer ${
                            selectedOrder?.id === order.id
                              ? 'border-teal-600 bg-teal-900/20'
                              : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-white font-medium text-sm">Order #{order.id.slice(0, 8)}</span>
                            <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(order.status)}`}>
                              {getStatusText(order.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mb-1">
                            {order.amount} {order.currency}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(order.created_at)}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}

                  {/* Contacts List */}
                  {viewType === 'contact' && (
                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {contacts.length === 0 && searchId && !loading && (
                        <div className="text-center py-8">
                          <i className="ri-inbox-line text-4xl text-gray-600 mb-3"></i>
                          <p className="text-gray-400">No inquiry found with this ID</p>
                        </div>
                      )}
                      
                      {contacts.map((contact) => (
                        <button
                          key={contact.id}
                          onClick={() => handleContactSelect(contact)}
                          className={`w-full text-left p-4 rounded-lg border transition-all cursor-pointer ${
                            selectedContact?.id === contact.id
                              ? 'border-teal-600 bg-teal-900/20'
                              : 'border-gray-700 bg-gray-800 hover:border-gray-600'
                          }`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <span className="text-white font-medium text-sm">{contact.subject}</span>
                            <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(contact.status)}`}>
                              {getStatusText(contact.status)}
                            </span>
                          </div>
                          <p className="text-sm text-gray-400 mb-1 line-clamp-2">
                            {contact.message}
                          </p>
                          <p className="text-xs text-gray-500">
                            {formatDate(contact.created_at)}
                          </p>
                        </button>
                      ))}
                    </div>
                  )}
                </Card>
              </div>

              {/* Message Thread */}
              <div className="lg:col-span-2">
                <Card className="p-0 h-full min-h-[600px] flex flex-col">
                  {selectedItem ? (
                    <>
                      {/* Header */}
                      <div className="p-6 border-b border-gray-700">
                        <div className="flex justify-between items-start">
                          <div>
                            {viewType === 'order' && selectedOrder && (
                              <>
                                <h3 className="text-lg font-semibold text-white mb-2">
                                  Order #{selectedOrder.id.slice(0, 8)} - {selectedOrder.customer_name}
                                </h3>
                                <div className="flex items-center space-x-4 text-sm text-gray-400">
                                  <span>{selectedOrder.amount} {selectedOrder.currency}</span>
                                  <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(selectedOrder.status)}`}>
                                    {getStatusText(selectedOrder.status)}
                                  </span>
                                  <span>{formatDate(selectedOrder.created_at)}</span>
                                </div>
                              </>
                            )}
                            {viewType === 'contact' && selectedContact && (
                              <>
                                <h3 className="text-lg font-semibold text-white mb-2">
                                  {selectedContact.subject} - {selectedContact.customer_name}
                                </h3>
                                <div className="flex items-center space-x-4 text-sm text-gray-400">
                                  <span className={`px-2 py-1 text-xs rounded-full text-white ${getStatusColor(selectedContact.status)}`}>
                                    {getStatusText(selectedContact.status)}
                                  </span>
                                  <span>{formatDate(selectedContact.created_at)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Messages */}
                      <div className="flex-1 p-6 overflow-y-auto max-h-96">
                        <div className="space-y-4">
                          {currentMessages.length === 0 && (
                            <div className="text-center py-8">
                              <i className="ri-chat-3-line text-4xl text-gray-600 mb-3"></i>
                              <p className="text-gray-400">No messages yet. Start the conversation!</p>
                            </div>
                          )}
                          
                          {currentMessages.map((message) => (
                            <div
                              key={message.id}
                              className={`flex ${message.sender_type === 'user' ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-xs lg:max-w-md px-4 py-3 rounded-lg ${
                                message.sender_type === 'user'
                                  ? 'bg-teal-600 text-white'
                                  : 'bg-gray-700 text-white'
                              }`}>
                                <p className="text-sm">{message.message}</p>
                                <p className={`text-xs mt-2 ${
                                  message.sender_type === 'user'
                                    ? 'text-teal-100'
                                    : 'text-gray-400'
                                }`}>
                                  {message.sender_type === 'user' ? 'You' : 'Support'} • {formatDate(message.created_at)}
                                </p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </div>
                      </div>

                      {/* Message Input */}
                      <div className="p-6 border-t border-gray-700">
                        <div className="flex space-x-3">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                viewType === 'order' ? sendOrderMessage() : sendContactMessage();
                              }
                            }}
                            className="flex-1 bg-gray-700 text-white rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                            placeholder="Type your message..."
                          />
                          <Button 
                            onClick={viewType === 'order' ? sendOrderMessage : sendContactMessage}
                            disabled={!newMessage.trim()}
                            className="whitespace-nowrap"
                          >
                            <i className="ri-send-plane-fill"></i>
                          </Button>
                        </div>
                        <p className="text-xs text-gray-400 mt-2">
                          <i className="ri-information-line mr-1"></i>
                          Our support team typically responds within 24 hours during business days.
                        </p>
                      </div>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <i className="ri-message-3-line text-6xl text-gray-600 mb-4"></i>
                        <h3 className="text-xl font-semibold text-white mb-2">
                          {viewType === 'order' ? 'Select an Order' : 'Select an Inquiry'}
                        </h3>
                        <p className="text-gray-400">
                          {viewType === 'order' 
                            ? 'Choose an order from the list to start messaging'
                            : 'Choose an inquiry from the list to start messaging'
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Help Section */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-6">Need Help?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-customer-service-2-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold mb-2 text-white">24/7 Support</h3>
                <p className="text-gray-400 text-sm">Our support team is available around the clock to assist you with any questions.</p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-shield-check-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold mb-2 text-white">Secure Communication</h3>
                <p className="text-gray-400 text-sm">All messages are encrypted and secure. Your privacy is our priority.</p>
              </div>
              
              <div className="bg-gray-900 rounded-xl p-6">
                <div className="w-12 h-12 bg-teal-600 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <i className="ri-time-line text-2xl text-white"></i>
                </div>
                <h3 className="font-semibold mb-2 text-white">Quick Response</h3>
                <p className="text-gray-400 text-sm">We aim to respond to all messages within 1 hour during business hours.</p>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
